import { useState, useRef, useEffect } from "react";
import gsap from "gsap";

interface ImageViewerProps {
  images: string[];
}

const ImageViewer = ({ images = [] }: ImageViewerProps) => {
  // Create thumbnails from the provided images
  const thumbnails = images.map((url, index) => ({
    id: index + 1,
    url,
  }));

  const [selectedImageId, setSelectedImageId] = useState(1);

  // Get the currently selected image URL
  const selectedImage = thumbnails.find(
    (thumb) => thumb.id === selectedImageId,
  )?.url;

  // Refs for GSAP animations
  const containerRef = useRef<HTMLDivElement>(null);
  const thumbsContainerRef = useRef<HTMLDivElement>(null);
  const mainImageRef = useRef<HTMLDivElement>(null);
  const thumbRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Set up thumbnail refs
  const setThumbRef = (el: HTMLDivElement | null, index: number) => {
    thumbRefs.current[index] = el;
  };

  // Function to scroll thumbnails
  const scrollThumbnails = (direction: "left" | "right") => {
    if (!thumbsContainerRef.current) return;

    const scrollAmount = 220; // Width of 2 thumbnails (100px each) + gaps
    const currentScroll = thumbsContainerRef.current.scrollLeft;
    const newScrollPosition =
      direction === "left"
        ? Math.max(0, currentScroll - scrollAmount)
        : currentScroll + scrollAmount;

    thumbsContainerRef.current.scrollTo({
      left: newScrollPosition,
      behavior: "smooth",
    });
  };

  // Animation for image change
  useEffect(() => {
    if (!mainImageRef.current) return;

    gsap.to(mainImageRef.current, {
      opacity: 0,
      scale: 0.98,
      duration: 0.2,
      onComplete: () => {
        gsap.to(mainImageRef.current, {
          opacity: 1,
          scale: 1,
          duration: 0.3,
        });
      },
    });

    // Highlight animation for the selected thumbnail
    thumbRefs.current.forEach((thumbRef, index) => {
      if (!thumbRef) return;

      if (thumbnails[index].id === selectedImageId) {
        gsap.fromTo(
          thumbRef,
          { scale: 0.95 },
          {
            scale: 1.05,
            duration: 0.3,
            ease: "power2.out",
            yoyo: true,
            repeat: 1,
          },
        );
      }
    });
  }, [selectedImageId, thumbnails]);

  return (
    <div className="flex flex-col w-full" ref={containerRef}>
      {/* Main image - full width with static height on all screen sizes */}
      <div
        className="w-full mb-4 border rounded-md overflow-hidden order-1"
        ref={mainImageRef}
      >
        <div className="relative w-full h-[500px]">
          {selectedImage ? (
            <img
              src={selectedImage}
              alt="Selected property"
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="absolute inset-0 bg-[#f5e6d1] flex items-center justify-center">
              <span className="text-[#0a2b25] text-6xl font-medium">480m²</span>
            </div>
          )}
        </div>
      </div>

      {/* Thumbnails container */}
      <div className="flex flex-col space-y-2 order-2">
        {/* Thumbnails row */}
        <div
          className="flex flex-row gap-2 overflow-x-auto hide-scrollbar"
          ref={thumbsContainerRef}
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {thumbnails.map((thumb, index) => (
            <div
              key={thumb.id}
              ref={(el) => setThumbRef(el, index)}
              className={`border rounded-md cursor-pointer overflow-hidden w-[100px] h-[100px] flex-shrink-0 transition-all duration-200 ${
                selectedImageId === thumb.id
                  ? "border-2 border-primary opacity-100 shadow-md"
                  : "border-gray-200 opacity-70"
              }`}
              onClick={() => setSelectedImageId(thumb.id)}
            >
              <div className="relative h-full w-full">
                {thumb.url ? (
                  <img
                    src={thumb.url}
                    alt={`Thumbnail ${thumb.id}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 bg-[#f5e6d1] flex items-center justify-center">
                    <span className="text-sm font-medium">480m²</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-end space-x-2 flex">
          <button
            className="bg-white hover:bg-gray-100 border border-gray-200 rounded-md p-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary"
            onClick={() => scrollThumbnails("left")}
            aria-label="Scroll thumbnails left"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <button
            className="bg-white hover:bg-gray-100 border border-gray-200 rounded-md p-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary"
            onClick={() => scrollThumbnails("right")}
            aria-label="Scroll thumbnails right"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageViewer;
